import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText, formatMoney, formatQty, formatDateTime, oeffneBeleg } from '../api';
import { useAuth } from '../auth';
import {
  Badge,
  Card,
  CheckField,
  Empty,
  Loading,
  Notice,
  NumberField,
  SelectField,
  TextField,
} from '../components/ui';
import type { Article, Component, Warengruppe, StockRow, Movement } from '../types';

type Kind = 'artikel' | 'variantenvater' | 'set';

const KIND_LABEL: Record<Kind, string> = {
  artikel: 'Artikel',
  variantenvater: 'Variantenvater',
  set: 'Set / Stückliste',
};

interface FormState {
  id: string | null;
  kind: Kind;
  parentArticleId: string;
  variantAttributes: string;
  variantValues: Record<string, string>;
  ownerId: string;
  sku: string;
  name: string;
  ean: string;
  manufacturerSku: string;
  unitsPerCarton: string;
  unit: string;
  minStock: number | '';
  purchasePrice: string;
  salePrice: string;
  einstand: string;
  vkBrutto: string;
  taxRate: string;
  weightG: number | '';
  requiresLot: boolean;
  requiresSerial: boolean;
  requiresBestBefore: boolean;
  hazmat: boolean;
  adrUnNummer: string;
  adrBenennung: string;
  adrKlasse: string;
  adrVerpackungsgruppe: string;
  adrKlassifizierungscode: string;
  adrGefahrzettel: string;
  adrTunnelcode: string;
  adrKategorie: string;
  adrMengeJeEinheit: string;
  adrBegrenzteMenge: boolean;
  productGroupId: string;
  active: boolean;
}

const emptyForm = (ownerId: string): FormState => ({
  id: null,
  kind: 'artikel',
  parentArticleId: '',
  variantAttributes: '',
  variantValues: {},
  ownerId,
  sku: '',
  name: '',
  ean: '',
  manufacturerSku: '',
  unitsPerCarton: '',
  unit: 'Stk',
  minStock: 0,
  purchasePrice: '',
  salePrice: '',
  einstand: '',
  vkBrutto: '',
  taxRate: '19',
  weightG: '',
  requiresLot: false,
  requiresSerial: false,
  requiresBestBefore: false,
  hazmat: false,
  adrUnNummer: '',
  adrBenennung: '',
  adrKlasse: '',
  adrVerpackungsgruppe: '',
  adrKlassifizierungscode: '',
  adrGefahrzettel: '',
  adrTunnelcode: '',
  adrKategorie: '',
  adrMengeJeEinheit: '',
  adrBegrenzteMenge: false,
  productGroupId: '',
  active: true,
});

/** "12,50" -> 1250 Cent. Leer bleibt leer. */
function toCents(value: string): number | null {
  const clean = value.replace(',', '.').trim();
  if (!clean) return null;
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

const fromCents = (cents: number | null) => (cents === null ? '' : (cents / 100).toFixed(2).replace('.', ','));

/**
 * Handelsspanne eines Artikels in Prozent, bezogen auf den Netto-VK.
 *
 * Genommen wird der beste vorhandene Preis: der Brutto-VK (auf netto
 * gerechnet), ersatzweise der hinterlegte Nettoverkaufspreis. Als Einstand
 * gilt der Einstandspreis, sonst der Einkaufspreis. Fehlt eine der beiden
 * Seiten, gibt es keine Spanne - dann bleibt die Zelle leer statt zu lügen.
 */
function spanneProzent(a: Article): number | null {
  const nettoVk =
    a.vk_brutto_cents && a.tax_rate_percent !== null
      ? Math.round(a.vk_brutto_cents / (1 + a.tax_rate_percent / 100))
      : a.sale_price_cents;
  const ek = a.einstand_cents ?? a.purchase_price_cents;
  if (!nettoVk || nettoVk <= 0 || ek === null) return null;
  return Math.round(((nettoVk - ek) / nettoVk) * 1000) / 10;
}

export function ArticlesPage() {
  const { owners, ownerId, can, verborgen } = useAuth();
  // Betriebsart-Signal: ist das Handelsmodul aktiv, wird nach Handel kalkuliert.
  const handelAktiv = !verborgen.has('/kalkulation');
  const mayEdit = can('admin', 'kaufmann', 'disponent');

  const [articles, setArticles] = useState<Article[] | null>(null);
  const [sp] = useSearchParams();
  const [search, setSearch] = useState(sp.get('q') ?? '');
  // Erneute Suche aus der Kopfzeile, während die Seite schon offen ist.
  useEffect(() => {
    const q = sp.get('q');
    if (q !== null) setSearch(q);
  }, [sp]);
  const [belowMin, setBelowMin] = useState(false);
  const [zeigeInaktive, setZeigeInaktive] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [parents, setParents] = useState<Article[]>([]);
  const [groups, setGroups] = useState<Warengruppe[]>([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [groupDeep, setGroupDeep] = useState(true);
  const [detail, setDetail] = useState<Article | null>(null);
  const [components, setComponents] = useState<Component[]>([]);
  const [componentTerm, setComponentTerm] = useState('');
  const [componentHits, setComponentHits] = useState<Article[]>([]);

  const load = useCallback(async () => {
    setError('');
    try {
      setArticles(
        await api<Article[]>('/api/articles', {
          query: {
            search,
            ownerId,
            // Standardmäßig nur aktive Artikel - deaktivierte (z. B. Demobestand)
            // bleiben aus der Liste, bis man sie ausdrücklich einblendet.
            active: zeigeInaktive ? undefined : 'true',
            belowMin: belowMin ? 'true' : undefined,
            groupId: groupFilter || undefined,
            groupTiefe: groupFilter && groupDeep ? 'true' : undefined,
          },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [search, ownerId, belowMin, groupFilter, groupDeep, zeigeInaktive]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  // Variantenväter für die Zuordnung, Komponenten für die Stückliste.
  useEffect(() => {
    api<Article[]>('/api/articles', { query: { limit: 500 } })
      .then((all) => setParents(all.filter((entry) => entry.kind === 'variantenvater')))
      .catch(() => setParents([]));
  }, [message]);

  useEffect(() => {
    api<Warengruppe[]>('/api/articles/groups')
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [message]);

  const formId = form?.id ?? null;
  const formKind = form?.kind ?? 'artikel';
  const formOwner = form?.ownerId ?? '';

  useEffect(() => {
    if (!formId || formKind !== 'set') {
      setComponents([]);
      return;
    }
    api<Component[]>(`/api/articles/${formId}/components`)
      .then(setComponents)
      .catch(() => setComponents([]));
  }, [formId, formKind]);

  useEffect(() => {
    if (!componentTerm.trim() || !formOwner) {
      setComponentHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', {
        query: { q: componentTerm.trim(), stockableOnly: 'true' },
      })
        .then((found) => setComponentHits(found.filter((a) => a.owner_id === formOwner)))
        .catch(() => setComponentHits([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [componentTerm, formOwner]);

  async function saveComponents(next: { componentArticleId: string; qty: number }[]) {
    if (!formId) return;
    setBusy(true);
    setError('');
    try {
      setComponents(
        await api<Component[]>(`/api/articles/${formId}/components`, {
          method: 'PUT',
          body: { components: next },
        }),
      );
      setMessage('Stückliste gespeichert.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function edit(article: Article) {
    setForm({
      id: article.id,
      kind: article.kind,
      parentArticleId: article.parent_article_id ?? '',
      variantAttributes: (article.variant_attributes ?? []).join(', '),
      variantValues: article.variant_values ?? {},
      ownerId: article.owner_id,
      sku: article.sku,
      name: article.name,
      ean: article.ean ?? '',
      manufacturerSku: article.manufacturer_sku ?? '',
      unitsPerCarton: article.units_per_carton != null ? String(article.units_per_carton) : '',
      unit: article.unit,
      minStock: article.min_stock,
      purchasePrice: fromCents(article.purchase_price_cents),
      salePrice: fromCents(article.sale_price_cents),
      einstand: fromCents(article.einstand_cents),
      vkBrutto: fromCents(article.vk_brutto_cents),
      taxRate: String(article.tax_rate_percent ?? 19),
      weightG: article.weight_g ?? '',
      requiresLot: article.requires_lot,
      requiresSerial: article.requires_serial,
      requiresBestBefore: article.requires_best_before,
      hazmat: article.hazmat,
      adrUnNummer: article.adr_un_nummer ?? '',
      adrBenennung: article.adr_benennung ?? '',
      adrKlasse: article.adr_klasse ?? '',
      adrVerpackungsgruppe: article.adr_verpackungsgruppe ?? '',
      adrKlassifizierungscode: article.adr_klassifizierungscode ?? '',
      adrGefahrzettel: article.adr_gefahrzettel ?? '',
      adrTunnelcode: article.adr_tunnelcode ?? '',
      adrKategorie: article.adr_kategorie === null ? '' : String(article.adr_kategorie),
      adrMengeJeEinheit:
        article.adr_menge_je_einheit === null ? '' : String(article.adr_menge_je_einheit),
      adrBegrenzteMenge: article.adr_begrenzte_menge,
      productGroupId: article.product_group_id ?? '',
      active: article.active,
    });
    setMessage('');
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        ownerId: form.ownerId,
        sku: form.sku,
        name: form.name,
        ean: form.ean || null,
        manufacturerSku: form.manufacturerSku || null,
        unitsPerCarton: form.unitsPerCarton.trim() === '' ? null : Number(form.unitsPerCarton),
        unit: form.unit,
        minStock: form.minStock === '' ? 0 : form.minStock,
        // Je nach Betriebsart wird nur ein Paar erfasst; das andere leitet der
        // Server ab. Was hier null bleibt, füllt er aus dem gesetzten Feld.
        purchasePriceCents: handelAktiv ? null : toCents(form.purchasePrice),
        salePriceCents: handelAktiv ? null : toCents(form.salePrice),
        einstandCents: handelAktiv ? toCents(form.einstand) : null,
        vkBruttoCents: handelAktiv ? toCents(form.vkBrutto) : null,
        taxRatePercent: handelAktiv && form.taxRate !== '' ? Number(form.taxRate) : undefined,
        weightG: form.weightG === '' ? null : form.weightG,
        requiresLot: form.requiresLot,
        requiresSerial: form.requiresSerial,
        requiresBestBefore: form.requiresBestBefore,
        hazmat: form.hazmat,
        // Ohne das Kennzeichen gehen die Angaben nicht mit - die Datenbank
        // weist eine UN-Nummer an einem Nicht-Gefahrgut zurück, und zwar zu
        // Recht: sie stünde auf keinem Beförderungspapier.
        adrUnNummer: form.hazmat ? form.adrUnNummer.trim() || null : null,
        adrBenennung: form.hazmat ? form.adrBenennung.trim() || null : null,
        adrKlasse: form.hazmat ? form.adrKlasse.trim() || null : null,
        adrVerpackungsgruppe: form.hazmat ? form.adrVerpackungsgruppe || null : null,
        adrKlassifizierungscode: form.hazmat ? form.adrKlassifizierungscode.trim() || null : null,
        adrGefahrzettel: form.hazmat ? form.adrGefahrzettel.trim() || null : null,
        adrTunnelcode: form.hazmat ? form.adrTunnelcode.trim() || null : null,
        adrKategorie: form.hazmat && form.adrKategorie !== '' ? Number(form.adrKategorie) : null,
        adrMengeJeEinheit:
          form.hazmat && form.adrMengeJeEinheit !== ''
            ? Number(form.adrMengeJeEinheit.replace(',', '.'))
            : null,
        adrBegrenzteMenge: form.hazmat ? form.adrBegrenzteMenge : false,
        active: form.active,
        kind: form.kind,
        parentArticleId: form.parentArticleId || null,
        variantAttributes:
          form.kind === 'variantenvater'
            ? form.variantAttributes
                .split(',')
                .map((entry) => entry.trim())
                .filter(Boolean)
            : null,
        variantValues: form.parentArticleId ? form.variantValues : null,
        productGroupId: form.productGroupId || null,
      };

      if (form.id) {
        const gespeichert = await api<{ sku: string }>(`/api/articles/${form.id}`, { method: 'PUT', body });
        setMessage(`Artikel ${gespeichert.sku} gespeichert.`);
      } else {
        const angelegt = await api<{ sku: string }>('/api/articles', { method: 'POST', body });
        setMessage(`Artikel ${angelegt.sku} angelegt.`);
      }
      setForm(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {form && (
        <Card title={form.id ? 'Artikel bearbeiten' : 'Neuer Artikel'}>
          <div className="form-grid">
            <label className="field">
              <span>Art</span>
              <select
                value={form.kind}
                onChange={(e) =>
                  setForm({ ...form, kind: e.target.value as Kind, parentArticleId: '' })
                }
                disabled={Boolean(form.id)}
              >
                {(Object.keys(KIND_LABEL) as Kind[]).map((kind) => (
                  <option key={kind} value={kind}>
                    {KIND_LABEL[kind]}
                  </option>
                ))}
              </select>
              <span className="small muted">
                {form.kind === 'variantenvater'
                  ? 'Klammer über Varianten, führt selbst keinen Bestand'
                  : form.kind === 'set'
                    ? 'wird im Auftrag in seine Komponenten aufgelöst'
                    : 'normaler, bestandsführender Artikel'}
              </span>
            </label>
            <SelectField
              label="Eigentümer der Ware"
              value={form.ownerId}
              onChange={(value) => setForm({ ...form, ownerId: value })}
              options={owners}
              render={(owner) => (owner.is_self ? `${owner.name} · eigene Ware` : owner.name)}
              allowEmpty={false}
            />
            <SelectField
              label="Warengruppe"
              value={form.productGroupId}
              onChange={(value) => setForm({ ...form, productGroupId: value })}
              options={groups.map((g) => ({ id: g.id, label: g.pfad }))}
              render={(o) => o.label}
              placeholder="— keine —"
            />
            <TextField
              label="Artikelnummer (optional)"
              value={form.sku}
              onChange={(value) => setForm({ ...form, sku: value })}
              hint="Leer lassen – wird automatisch fortlaufend vergeben"
            />
            <TextField
              label="Bezeichnung"
              value={form.name}
              onChange={(value) => setForm({ ...form, name: value })}
            />
            <TextField
              label="EAN / GTIN"
              value={form.ean}
              onChange={(value) => setForm({ ...form, ean: value })}
              hint="wird beim Scannen zuerst geprüft"
            />
            <TextField
              label="Herstellernummer"
              value={form.manufacturerSku}
              onChange={(value) => setForm({ ...form, manufacturerSku: value })}
              hint="Bestell-/Herstellernummer, z. B. 501 00 84-01"
            />
            <TextField
              label="Inhalt je Karton (Stück)"
              value={form.unitsPerCarton}
              onChange={(value) => setForm({ ...form, unitsPerCarton: value.replace(/[^0-9]/g, '') })}
              hint="Optional. Erlaubt kartonweises Einlagern (Kartons × Inhalt = Bestand)."
            />
            <TextField
              label="Einheit"
              value={form.unit}
              onChange={(value) => setForm({ ...form, unit: value })}
            />
            <NumberField
              label="Mindestbestand"
              value={form.minStock}
              onChange={(value) => setForm({ ...form, minStock: value })}
            />
            {handelAktiv ? (
              <>
                <TextField
                  label="Einstand netto (€)"
                  value={form.einstand}
                  onChange={(value) => setForm({ ...form, einstand: value })}
                  hint="Einkaufspreis nach Abzügen — Basis der Spanne"
                />
                <TextField
                  label="VK brutto (€)"
                  value={form.vkBrutto}
                  onChange={(value) => setForm({ ...form, vkBrutto: value })}
                  hint="Ladenpreis inkl. Steuer"
                />
                <TextField
                  label="Steuersatz (%)"
                  value={form.taxRate}
                  onChange={(value) => setForm({ ...form, taxRate: value })}
                  hint="19 oder 7"
                />
              </>
            ) : (
              <>
                <TextField
                  label="Einkaufspreis (€)"
                  value={form.purchasePrice}
                  onChange={(value) => setForm({ ...form, purchasePrice: value })}
                  hint="nur bei eigener Ware relevant"
                />
                <TextField
                  label="Verkaufspreis (€)"
                  value={form.salePrice}
                  onChange={(value) => setForm({ ...form, salePrice: value })}
                />
              </>
            )}
            <NumberField
              label="Gewicht (g)"
              value={form.weightG}
              onChange={(value) => setForm({ ...form, weightG: value })}
            />
          </div>

          {form.kind === 'variantenvater' && (
            <TextField
              label="Variantenachsen"
              value={form.variantAttributes}
              onChange={(value) => setForm({ ...form, variantAttributes: value })}
              hint="durch Komma getrennt, z. B. Grösse, Farbe"
            />
          )}

          {form.kind === 'artikel' && parents.length > 0 && (
            <>
              <label className="field">
                <span>Variante von (optional)</span>
                <select
                  value={form.parentArticleId}
                  onChange={(e) => setForm({ ...form, parentArticleId: e.target.value })}
                >
                  <option value="">— eigenständiger Artikel —</option>
                  {parents
                    .filter((parent) => parent.owner_id === form.ownerId)
                    .map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.sku} · {parent.name}
                      </option>
                    ))}
                </select>
              </label>

              {form.parentArticleId && (
                <div className="form-grid">
                  {(parents.find((p) => p.id === form.parentArticleId)?.variant_attributes ?? []).map(
                    (axis) => (
                      <TextField
                        key={axis}
                        label={axis}
                        value={form.variantValues[axis] ?? ''}
                        onChange={(value) =>
                          setForm({
                            ...form,
                            variantValues: { ...form.variantValues, [axis]: value },
                          })
                        }
                      />
                    ),
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 6 }}>
            <CheckField
              label="Chargenpflichtig"
              checked={form.requiresLot}
              onChange={(checked) => setForm({ ...form, requiresLot: checked })}
            />
            <CheckField
              label="MHD führen"
              checked={form.requiresBestBefore}
              onChange={(checked) => setForm({ ...form, requiresBestBefore: checked })}
            />
            <CheckField
              label="Seriennummernpflichtig"
              checked={form.requiresSerial}
              onChange={(checked) => setForm({ ...form, requiresSerial: checked })}
            />
            <CheckField
              label="Gefahrgut"
              checked={form.hazmat}
              onChange={(checked) => setForm({ ...form, hazmat: checked })}
            />
            <CheckField
              label="Aktiv"
              checked={form.active}
              onChange={(checked) => setForm({ ...form, active: checked })}
            />
          </div>

          {form.hazmat && (
            <div
              style={{
                borderTop: '1px solid var(--line)',
                marginTop: 16,
                paddingTop: 14,
              }}
            >
              <h4 style={{ margin: '0 0 4px' }}>Gefahrgut nach ADR</h4>
              <div className="small muted" style={{ marginBottom: 12 }}>
                Diese Angaben gehören ins Beförderungspapier — in der Reihenfolge UN-Nummer,
                Benennung, Gefahrzettel, Verpackungsgruppe, Tunnelcode. Die Beförderungskategorie
                entscheidet über die Freistellung nach der 1000-Punkte-Regel; ohne sie kann der
                Auftrag nicht bewertet werden.
              </div>

              <div className="form-grid">
                <TextField
                  label="UN-Nummer"
                  value={form.adrUnNummer}
                  onChange={(v) => setForm({ ...form, adrUnNummer: v })}
                  placeholder="1263"
                  hint="vierstellig, ohne das Kürzel UN"
                />
                <TextField
                  label="Offizielle Benennung"
                  value={form.adrBenennung}
                  onChange={(v) => setForm({ ...form, adrBenennung: v })}
                  placeholder="FARBE"
                  hint="wie im ADR-Verzeichnis, nicht der Handelsname"
                />
                <TextField
                  label="Klasse"
                  value={form.adrKlasse}
                  onChange={(v) => setForm({ ...form, adrKlasse: v })}
                  placeholder="3"
                />
                <label className="field">
                  <span>Verpackungsgruppe</span>
                  <select
                    value={form.adrVerpackungsgruppe}
                    onChange={(e) => setForm({ ...form, adrVerpackungsgruppe: e.target.value })}
                  >
                    <option value="">— keine —</option>
                    <option value="I">I — hohe Gefahr</option>
                    <option value="II">II — mittlere Gefahr</option>
                    <option value="III">III — geringe Gefahr</option>
                  </select>
                </label>
                <label className="field">
                  <span>Beförderungskategorie</span>
                  <select
                    value={form.adrKategorie}
                    onChange={(e) => setForm({ ...form, adrKategorie: e.target.value })}
                  >
                    <option value="">— unbekannt —</option>
                    <option value="0">0 — keine Freistellung möglich</option>
                    <option value="1">1 — Faktor 50, höchstens 20</option>
                    <option value="2">2 — Faktor 3, höchstens 333</option>
                    <option value="3">3 — Faktor 1, höchstens 1000</option>
                    <option value="4">4 — unbegrenzt</option>
                  </select>
                </label>
                <TextField
                  label="Menge je Einheit"
                  value={form.adrMengeJeEinheit}
                  onChange={(v) => setForm({ ...form, adrMengeJeEinheit: v })}
                  placeholder="5"
                  hint="Netto in kg oder Litern — geht in die Punkterechnung ein"
                />
                <TextField
                  label="Gefahrzettel"
                  value={form.adrGefahrzettel}
                  onChange={(v) => setForm({ ...form, adrGefahrzettel: v })}
                  placeholder="3"
                  hint="wenn abweichend von der Klasse, z. B. 8+3"
                />
                <TextField
                  label="Klassifizierungscode"
                  value={form.adrKlassifizierungscode}
                  onChange={(v) => setForm({ ...form, adrKlassifizierungscode: v })}
                  placeholder="F1"
                />
                <TextField
                  label="Tunnelbeschränkungscode"
                  value={form.adrTunnelcode}
                  onChange={(v) => setForm({ ...form, adrTunnelcode: v })}
                  placeholder="D/E"
                />
              </div>

              <CheckField
                label="In begrenzten Mengen nach Kapitel 3.4"
                checked={form.adrBegrenzteMenge}
                onChange={(checked) => setForm({ ...form, adrBegrenzteMenge: checked })}
              />
              <div className="small muted">
                Dann entfällt die Punkterechnung, dafür gelten eigene Kennzeichnungspflichten am
                Versandstück.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={save} disabled={busy}>
              {busy ? 'Speichern …' : 'Speichern'}
            </button>
            <button onClick={() => setForm(null)}>Abbrechen</button>
          </div>

          {form.kind === 'set' && !form.id && (
            <Notice kind="info">
              Erst speichern, danach lässt sich die Stückliste zusammenstellen.
            </Notice>
          )}

          {form.kind === 'set' && form.id && (
            <>
              <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '18px 0 14px' }} />
              <strong className="small">Stückliste</strong>
              <p className="small muted" style={{ marginTop: 4 }}>
                Komponenten müssen bestandsführende Artikel desselben Eigentümers sein. Beim Auftrag
                wird das Set in genau diese Positionen aufgelöst.
              </p>

              <label className="field">
                <span>Komponente suchen</span>
                <input
                  value={componentTerm}
                  onChange={(e) => setComponentTerm(e.target.value)}
                  placeholder="Artikelnummer oder Bezeichnung"
                />
              </label>
              {componentHits.length > 0 && (
                <div className="pick-list">
                  {componentHits
                    .filter((hit) => !components.some((c) => c.component_article_id === hit.id))
                    .map((hit) => (
                      <button
                        key={hit.id}
                        onClick={() => {
                          void saveComponents([
                            ...components.map((c) => ({
                              componentArticleId: c.component_article_id,
                              qty: c.qty,
                            })),
                            { componentArticleId: hit.id, qty: 1 },
                          ]);
                          setComponentTerm('');
                          setComponentHits([]);
                        }}
                      >
                        <strong>{hit.name}</strong>
                        <div className="small muted mono">{hit.sku}</div>
                      </button>
                    ))}
                </div>
              )}

              {components.length > 0 && (
                <table style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>Komponente</th>
                      <th className="num">Menge je Set</th>
                      <th className="num">Bestand</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((component) => (
                      <tr key={component.id}>
                        <td>
                          <span className="mono">{component.sku}</span> {component.article_name}
                        </td>
                        <td className="num" style={{ width: 130 }}>
                          <input
                            style={{ textAlign: 'right' }}
                            defaultValue={component.qty}
                            onBlur={(e) => {
                              const qty = Number(e.target.value.replace(',', '.'));
                              if (!Number.isFinite(qty) || qty <= 0) return;
                              void saveComponents(
                                components.map((c) => ({
                                  componentArticleId: c.component_article_id,
                                  qty: c.id === component.id ? qty : c.qty,
                                })),
                              );
                            }}
                          />
                        </td>
                        <td className="num muted">{formatQty(component.stock_total)}</td>
                        <td className="num">
                          <button
                            className="small ghost"
                            disabled={components.length === 1}
                            onClick={() =>
                              void saveComponents(
                                components
                                  .filter((c) => c.id !== component.id)
                                  .map((c) => ({
                                    componentArticleId: c.component_article_id,
                                    qty: c.qty,
                                  })),
                              )
                            }
                          >
                            entfernen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Card>
      )}

      {detail && !form && (
        <ArtikelLese
          artikel={detail}
          mayEdit={mayEdit}
          onEdit={() => {
            edit(detail);
            setDetail(null);
          }}
          onClose={() => setDetail(null)}
          onDelete={async () => {
            if (!window.confirm(`Artikel „${detail.sku} — ${detail.name}" wirklich löschen?`)) return;
            setError('');
            try {
              await api(`/api/articles/${detail.id}`, { method: 'DELETE' });
              setMessage(`Artikel ${detail.sku} gelöscht.`);
              setDetail(null);
              await load();
            } catch (err) {
              setError(errorText(err));
            }
          }}
        />
      )}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 280 }}>
          <span>Suche</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nummer, Bezeichnung oder EAN"
          />
        </label>
        <CheckField label="Nur unter Mindestbestand" checked={belowMin} onChange={setBelowMin} />
        <CheckField label="Auch inaktive zeigen" checked={zeigeInaktive} onChange={setZeigeInaktive} />
        {groups.length > 0 && (
          <>
            <label className="field" style={{ minWidth: 200 }}>
              <span>Warengruppe</span>
              <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                <option value="">alle</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {'  '.repeat(g.ebene)}
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            {groupFilter && (
              <CheckField label="mit Untergruppen" checked={groupDeep} onChange={setGroupDeep} />
            )}
          </>
        )}
        <span className="spacer" />
        {mayEdit && (
          <button className="primary" onClick={() => setForm(emptyForm(ownerId || owners[0]?.id || ''))}>
            Neuer Artikel
          </button>
        )}
      </div>

      <Card padded={false}>
        {!articles ? (
          <Loading />
        ) : articles.length === 0 ? (
          <Empty>Keine Artikel gefunden.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Bezeichnung</th>
                  <th>Eigentümer</th>
                  <th>Merkmale</th>
                  <th className="num">Bestand</th>
                  <th className="num">Minimum</th>
                  <th className="num">EK</th>
                  <th className="num">Spanne</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr
                    key={article.id}
                    onClick={() => setDetail(article)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono">{article.sku}</td>
                    <td>
                      {article.kind === 'variantenvater' && (
                        <>
                          <Badge kind="info">Vater</Badge>{' '}
                        </>
                      )}
                      {article.kind === 'set' && (
                        <>
                          <Badge kind="warn">Set</Badge>{' '}
                        </>
                      )}
                      {article.name}
                      {article.variant_values && (
                        <span className="small muted">
                          {' '}
                          (
                          {Object.entries(article.variant_values)
                            .map(([axis, value]) => `${axis}: ${value}`)
                            .join(', ')}
                          )
                        </span>
                      )}
                      {!article.active && (
                        <>
                          {' '}
                          <Badge kind="bad">inaktiv</Badge>
                        </>
                      )}
                    </td>
                    <td className="small">{article.owner_name}</td>
                    <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {article.requires_lot && <Badge kind="info">Charge</Badge>}
                      {article.requires_serial && <Badge kind="info">Serie</Badge>}
                      {article.requires_best_before && <Badge kind="info">MHD</Badge>}
                      {article.hazmat && <Badge kind="warn">Gefahrgut</Badge>}
                    </td>
                    <td
                      className="num"
                      style={
                        article.min_stock > 0 && article.stock_total < article.min_stock
                          ? { color: 'var(--bad)', fontWeight: 600 }
                          : undefined
                      }
                    >
                      {article.kind === 'variantenvater'
                        ? `${article.variant_count} Varianten`
                        : article.kind === 'set'
                          ? `${formatQty(article.buildable ?? 0)} baubar`
                          : formatQty(article.stock_total, article.unit)}
                    </td>
                    <td className="num muted">{article.min_stock || '—'}</td>
                    <td className="num">
                      {formatMoney(article.einstand_cents ?? article.purchase_price_cents)}
                    </td>
                    <td className="num">
                      {spanneProzent(article) !== null ? `${spanneProzent(article)} %` : '—'}
                    </td>
                    <td className="num">
                      <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button
                          className="small ghost"
                          title="Etikett mit QR und Barcode drucken"
                          onClick={(e) => {
                            e.stopPropagation();
                            oeffneBeleg(`/api/printing/label/article/${article.id}`).catch((err) =>
                              setError(errorText(err)),
                            );
                          }}
                        >
                          Etikett
                        </button>
                        {mayEdit && (
                          <button
                            className="small ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              edit(article);
                            }}
                          >
                            bearbeiten
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/**
 * Leseansicht eines Artikels.
 *
 * Der Wunsch dahinter: einen Artikel ansehen, ohne ihn zu bearbeiten - im
 * Handel oft nur, um Bestand und Marge zu prüfen. Deshalb steht die
 * Kalkulation gleichberechtigt neben den Stammdaten, und alles ist read-only.
 */
function ZeileWert({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ display: 'flex', gap: 10, padding: '3px 0' }}>
      <div className="small muted" style={{ minWidth: 150 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

interface DetailZusage {
  id: string;
  bezeichnung: string;
  zahler_name: string;
  art: 'betrag' | 'prozent';
  wert: number;
  gueltig_von: string;
  gueltig_bis: string;
  betrag_je_einheit_cents: number;
  aktiv: boolean;
}

type DetailTab = 'uebersicht' | 'preise' | 'bestand' | 'bewegungen';

/**
 * Artikel-Detailansicht mit Reitern.
 *
 * Der Gedanke - wie in JTL & Co.: Der Artikel ist der Ort, an dem alles ueber
 * ihn zusammenlaeuft. Statt Preise, Marge, Bestand und Bewegungen ueber eigene
 * Menuepunkte zu suchen, liegen sie hier, einen Klick entfernt. Die Reiter laden
 * ihre Daten erst beim Oeffnen - wer nur die Stammdaten sehen will, holt nicht
 * die Bewegungshistorie mit.
 */
function ArtikelLese({
  artikel,
  mayEdit,
  onEdit,
  onClose,
  onDelete,
}: {
  artikel: Article;
  mayEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>('uebersicht');
  const [bestand, setBestand] = useState<StockRow[] | null>(null);
  const [bewegungen, setBewegungen] = useState<Movement[] | null>(null);
  const [zusagen, setZusagen] = useState<DetailZusage[] | null>(null);
  const [zielspanne, setZielspanne] = useState('30');
  const [zielpreis, setZielpreis] = useState<number | null>(null);
  const [rechneFehler, setRechneFehler] = useState('');

  const nettoVk =
    artikel.vk_brutto_cents && artikel.tax_rate_percent !== null
      ? Math.round(artikel.vk_brutto_cents / (1 + artikel.tax_rate_percent / 100))
      : artikel.sale_price_cents;
  const ek = artikel.einstand_cents ?? artikel.purchase_price_cents;
  const spanne = spanneProzent(artikel);
  const aufschlag =
    ek !== null && ek > 0 && nettoVk ? Math.round(((nettoVk - ek) / ek) * 1000) / 10 : null;
  const masse = [artikel.length_mm, artikel.width_mm, artikel.height_mm].some((m) => m !== null)
    ? `${artikel.length_mm ?? '—'} × ${artikel.width_mm ?? '—'} × ${artikel.height_mm ?? '—'} mm`
    : '';

  useEffect(() => {
    if (tab === 'bestand' && bestand === null) {
      api<StockRow[]>(`/api/stock/availability/${artikel.id}`).then(setBestand).catch(() => setBestand([]));
    }
    if (tab === 'bewegungen' && bewegungen === null) {
      api<Movement[]>('/api/stock/movements', { query: { articleId: artikel.id, limit: 25 } })
        .then(setBewegungen)
        .catch(() => setBewegungen([]));
    }
    if (tab === 'preise' && zusagen === null) {
      api<DetailZusage[]>('/api/articles/avu/zusagen', { query: { articleId: artikel.id } })
        .then(setZusagen)
        .catch(() => setZusagen([]));
    }
  }, [tab, artikel.id, bestand, bewegungen, zusagen]);

  async function rechneZielpreis() {
    setRechneFehler('');
    if (ek === null || ek <= 0) {
      setRechneFehler('Ohne Einstandspreis lässt sich kein Zielpreis rechnen.');
      return;
    }
    try {
      const r = await api<{ vkBruttoCents: number | null }>('/api/articles/kalkulation/zielpreis', {
        method: 'POST',
        body: { einstandCents: ek, zielspanne: Number(zielspanne), steuersatz: artikel.tax_rate_percent ?? 19 },
      });
      setZielpreis(r.vkBruttoCents);
    } catch (err) {
      setRechneFehler(errorText(err));
    }
  }

  const reiter: [DetailTab, string][] = [
    ['uebersicht', 'Übersicht'],
    ['preise', 'Preise & Marge'],
    ['bestand', 'Bestand'],
    ['bewegungen', 'Bewegungen'],
  ];

  return (
    <Card
      title={`${artikel.sku} — ${artikel.name}${artikel.active ? '' : '  (inaktiv)'}`}
      actions={
        <span style={{ display: 'flex', gap: 8 }}>
          <button
            title="Etikett mit QR und Barcode drucken"
            onClick={() => oeffneBeleg(`/api/printing/label/article/${artikel.id}`).catch(() => {})}
          >
            Etikett drucken
          </button>
          {mayEdit && (
            <button className="primary" onClick={onEdit}>
              Bearbeiten
            </button>
          )}
          {mayEdit && (
            <button
              onClick={onDelete}
              title="Nur möglich, wenn der Artikel nie verwendet wurde"
              style={{ color: 'var(--bad)', borderColor: 'var(--bad-line)' }}
            >
              Löschen
            </button>
          )}
          <button onClick={onClose}>Schließen</button>
        </span>
      }
    >
      <div className="tabs" style={{ marginBottom: 16 }}>
        {reiter.map(([wert, label]) => (
          <button key={wert} className={tab === wert ? 'active' : ''} onClick={() => setTab(wert)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'uebersicht' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Stammdaten</div>
            <ZeileWert label="Eigentümer">{artikel.owner_name}</ZeileWert>
            <ZeileWert label="Warengruppe">{artikel.product_group_name}</ZeileWert>
            <ZeileWert label="Einheit">{artikel.unit}</ZeileWert>
            <ZeileWert label="EAN">{artikel.ean}</ZeileWert>
            <ZeileWert label="Herstellernummer">{artikel.manufacturer_sku}</ZeileWert>
            <ZeileWert label="Inhalt je Karton">
              {artikel.units_per_carton ? `${artikel.units_per_carton} ${artikel.unit}` : ''}
            </ZeileWert>
            <ZeileWert label="Hersteller">{artikel.manufacturer_name}</ZeileWert>
            <ZeileWert label="Gewicht">{artikel.weight_g ? `${artikel.weight_g} g` : ''}</ZeileWert>
            <ZeileWert label="Maße (L×B×H)">{masse}</ZeileWert>
            <ZeileWert label="Beschreibung">{artikel.description}</ZeileWert>
          </div>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Merkmale</div>
            <ZeileWert label="Bestand gesamt">
              <strong>{formatQty(artikel.stock_total, artikel.unit)}</strong>
            </ZeileWert>
            <ZeileWert label="Mindestbestand">{artikel.min_stock || '—'}</ZeileWert>
            <ZeileWert label="Kennzeichen">
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {artikel.requires_lot && <Badge kind="info">Charge</Badge>}
                {artikel.requires_serial && <Badge kind="info">Serie</Badge>}
                {artikel.requires_best_before && <Badge kind="info">MHD</Badge>}
                {artikel.hazmat && <Badge kind="warn">Gefahrgut</Badge>}
                {!artikel.requires_lot &&
                  !artikel.requires_serial &&
                  !artikel.requires_best_before &&
                  !artikel.hazmat &&
                  '—'}
              </span>
            </ZeileWert>
            {artikel.hazmat && <ZeileWert label="Beförderung">{artikel.adr_angabe ?? 'unvollständig'}</ZeileWert>}
          </div>
        </div>
      )}

      {tab === 'preise' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Kalkulation</div>
            <ZeileWert label="Einstand / EK">{formatMoney(ek)}</ZeileWert>
            <ZeileWert label="VK brutto">{formatMoney(artikel.vk_brutto_cents)}</ZeileWert>
            <ZeileWert label="VK netto">{formatMoney(nettoVk)}</ZeileWert>
            <ZeileWert label="Steuersatz">
              {artikel.tax_rate_percent !== null ? `${artikel.tax_rate_percent} %` : ''}
            </ZeileWert>
            <ZeileWert label="Handelsspanne">
              {spanne !== null ? <strong>{spanne} %</strong> : '— (VK oder Einstand fehlt)'}
            </ZeileWert>
            <ZeileWert label="Aufschlag">{aufschlag !== null ? `${aufschlag} %` : '—'}</ZeileWert>
          </div>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Zielpreis-Rechner</div>
            <div className="small muted" style={{ marginBottom: 8 }}>
              Welcher VK brutto ergibt eine Wunschspanne — beim Einstand {formatMoney(ek)}?
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <TextField label="Zielspanne (%)" value={zielspanne} onChange={setZielspanne} />
              <button onClick={rechneZielpreis}>Rechnen</button>
            </div>
            {zielpreis !== null && (
              <div style={{ marginTop: 10 }}>
                Nötiger VK brutto: <strong>{formatMoney(zielpreis)}</strong>
              </div>
            )}
            {rechneFehler && (
              <div className="small" style={{ color: 'var(--bad)', marginTop: 8 }}>{rechneFehler}</div>
            )}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>
              Abverkaufsunterstützung (Herstellerzusagen)
            </div>
            {zusagen === null ? (
              <Loading />
            ) : zusagen.length === 0 ? (
              <div className="small muted">Keine AVU-Zusage für diesen Artikel.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Zusage</th>
                      <th>Hersteller</th>
                      <th className="num">je Stück</th>
                      <th>gültig bis</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {zusagen.map((z) => (
                      <tr key={z.id}>
                        <td>{z.bezeichnung}</td>
                        <td className="small">{z.zahler_name}</td>
                        <td className="num">{formatMoney(z.betrag_je_einheit_cents)}</td>
                        <td className="small muted">{z.gueltig_bis}</td>
                        <td>{!z.aktiv && <Badge kind="bad">inaktiv</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'bestand' &&
        (bestand === null ? (
          <Loading />
        ) : bestand.length === 0 ? (
          <Empty>Kein Bestand auf einem realen Platz.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lager</th>
                  <th>Platz</th>
                  <th>Charge</th>
                  <th>MHD</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {bestand.map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.warehouse_code}</td>
                    <td className="mono">{r.location_code}</td>
                    <td className="small">{r.lot_code ?? '—'}</td>
                    <td className="small muted">{r.best_before ?? '—'}</td>
                    <td className="num">{formatQty(r.qty, r.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'bewegungen' &&
        (bewegungen === null ? (
          <Loading />
        ) : bewegungen.length === 0 ? (
          <Empty>Noch keine Bewegung.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Vorgang</th>
                  <th className="num">Menge</th>
                  <th>von → nach</th>
                  <th>Beleg</th>
                </tr>
              </thead>
              <tbody>
                {bewegungen.map((m) => (
                  <tr key={m.id}>
                    <td className="small muted">{formatDateTime(m.booked_at)}</td>
                    <td className="small">{m.reason}</td>
                    <td className="num">{formatQty(m.qty, m.unit)}</td>
                    <td className="small mono">{(m.from_code ?? '—') + ' → ' + (m.to_code ?? '—')}</td>
                    <td className="small muted">{m.document_ref ?? m.note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </Card>
  );
}
